import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { FilterIcon, GridIcon, ListIcon, StarIcon, SearchIcon, BoxIcon, GlobeIcon, AlertCircleIcon, HeartIcon, HeartHandshakeIcon, BookOpenIcon, MapPinIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, CheckIcon, TruckIcon, CalendarIcon } from 'lucide-react';
import { bookBoxes } from '../BookBoxDetails';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
// Mock data for content tags and age ratings
const mockContentData = {
  'Bookish & Spice': {
    ageRating: '18+',
    tropes: ['Enemies to Lovers', 'Forced Proximity', 'Slow Burn'],
    representation: ['LGBTQ+', 'BIPOC'],
    warnings: ['Sexual Content', 'Strong Language']
  },
  'Dark & Disturbed': {
    ageRating: '18+',
    tropes: ['Dark Romance', 'Psychological Thriller', 'Morally Grey'],
    representation: ['Mental Health', 'Disability'],
    warnings: ['Violence', 'Trauma', 'Self-Harm']
  },
  'Fantasy Realm Crate': {
    ageRating: '13+',
    tropes: ['Chosen One', 'Found Family', 'Magic School'],
    representation: ['LGBTQ+', 'Neurodiversity'],
    warnings: ['Fantasy Violence', 'Mild Language']
  },
  'Mystery Maven Box': {
    ageRating: '16+',
    tropes: ['Unreliable Narrator', 'Locked Room', 'Amateur Detective'],
    representation: ['BIPOC', 'Women in STEM'],
    warnings: ['Violence', 'Death', 'Kidnapping']
  },
  'Otaku Reads Crate': {
    ageRating: 'All Ages',
    tropes: ['Coming of Age', 'School Life', 'Tournament Arc'],
    representation: ['Japanese Culture', 'LGBTQ+'],
    warnings: ['Mild Fantasy Violence']
  },
  'K-Drama Book Box': {
    ageRating: '13+',
    tropes: ['Fake Dating', 'Childhood Friends', 'Forbidden Love'],
    representation: ['Korean Culture', 'International'],
    warnings: ['Mild Language', 'Family Conflict']
  },
  'Aussie Lit Crate': {
    ageRating: 'All Ages',
    tropes: ['Coming of Age', 'Small Town', 'Historical'],
    representation: ['Indigenous', 'LGBTQ+', 'Rural'],
    warnings: ['Historical Trauma', 'Colonialism']
  },
  'Latin Lit Love': {
    ageRating: '16+',
    tropes: ['Family Saga', 'Magical Realism', 'Political'],
    representation: ['Latin American', 'Indigenous', 'BIPOC'],
    warnings: ['Political Violence', 'Historical Trauma']
  }
};
type ViewMode = 'grid' | 'list';
const BookBoxes = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedFrequencies, setSelectedFrequencies] = useState<string[]>([]);
  const [expandedBoxId, setExpandedBoxId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  // Sidebar states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Extract all unique themes from book boxes
  const allThemes = Array.from(new Set(bookBoxes.flatMap(box => box.theme.split(', ').map(theme => theme.trim())))).sort();
  // Extract all unique countries from book boxes
  const allCountries = Array.from(new Set(bookBoxes.map(box => box.locationCountry))).sort();
  const allFrequencies = ['Monthly', 'Bi-Monthly', 'Quarterly'];
  // Filter book boxes based on search term, selected themes and countries
  const filteredBookBoxes = bookBoxes.filter(box => {
    const matchesSearch = searchTerm === '' || box.boxName.toLowerCase().includes(searchTerm.toLowerCase()) || box.description.toLowerCase().includes(searchTerm.toLowerCase()) || box.theme.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesThemes = selectedThemes.length === 0 || selectedThemes.some(theme => box.theme.toLowerCase().includes(theme.toLowerCase()));
    const matchesCountries = selectedCountries.length === 0 || selectedCountries.includes(box.locationCountry);
    const matchesFrequency = selectedFrequencies.length === 0 || selectedFrequencies.includes(box.frequency);
    return matchesSearch && matchesThemes && matchesCountries && matchesFrequency;
  });
  const toggleTheme = (theme: string) => {
    setSelectedThemes(prev => prev.includes(theme) ? prev.filter(t => t !== theme) : [...prev, theme]);
  };
  const toggleCountry = (country: string) => {
    setSelectedCountries(prev => prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]);
  };
  const toggleFrequency = (frequency: string) => {
    setSelectedFrequencies(prev => prev.includes(frequency) ? prev.filter(f => f !== frequency) : [...prev, frequency]);
  };
  const toggleExpand = (boxName: string) => {
    setExpandedBoxId(expandedBoxId === boxName ? null : boxName);
  };
  const resetFilters = () => {
    setSelectedThemes([]);
    setSelectedCountries([]);
    setSelectedFrequencies([]);
    setSearchTerm('');
  };
  // Format currency
  const formatCurrency = (price: string, currency: string) => {
    const numericPrice = parseFloat(price);
    switch (currency) {
      case 'USD':
        return `$${numericPrice.toFixed(2)}`;
      case 'GBP':
        return `£${numericPrice.toFixed(2)}`;
      case 'AUD':
        return `A$${numericPrice.toFixed(2)}`;
      case 'CAD':
        return `C$${numericPrice.toFixed(2)}`;
      case 'JPY':
        return `¥${numericPrice}`;
      case 'KRW':
        return `₩${numericPrice}`;
      default:
        return `${numericPrice.toFixed(2)} ${currency}`;
    }
  };
  // Helper function to get content data for a box
  const getContentData = (boxName: string) => {
    return mockContentData[boxName as keyof typeof mockContentData] || {
      ageRating: 'All Ages',
      tropes: [],
      representation: [],
      warnings: []
    };
  };
  // Helper for content tag pills
  const ContentTagPill = ({
    type,
    label
  }: {
    type: 'trope' | 'rep' | 'warning' | 'theme' | 'setting';
    label: string;
  }) => {
    let colors = '';
    let Icon = BookOpenIcon;
    switch (type) {
      case 'trope':
        colors = dk.isDark ? 'bg-indigo-900/30 text-indigo-300 border-indigo-800/50' : 'bg-indigo-50 text-indigo-700 border-indigo-100';
        Icon = HeartHandshakeIcon;
        break;
      case 'rep':
        colors = dk.isDark ? 'bg-teal-900/30 text-teal-300 border-teal-800/50' : 'bg-teal-50 text-teal-700 border-teal-100';
        Icon = HeartIcon;
        break;
      case 'warning':
        colors = dk.isDark ? 'bg-amber-900/30 text-amber-300 border-amber-800/50' : 'bg-amber-50 text-amber-700 border-amber-100';
        Icon = AlertCircleIcon;
        break;
      case 'theme':
        colors = dk.isDark ? 'bg-blue-900/30 text-blue-300 border-blue-800/50' : 'bg-blue-50 text-blue-700 border-blue-100';
        Icon = BookOpenIcon;
        break;
      case 'setting':
        colors = dk.isDark ? 'bg-green-900/30 text-green-300 border-green-800/50' : 'bg-green-50 text-green-700 border-green-100';
        Icon = MapPinIcon;
        break;
    }
    return <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${colors}`} data-id="element-7478" data-mp="mzp28iz05ejv">
        <Icon className="w-3 h-3 mr-1.5" data-id="element-7479" data-mp="1ueacuxtf2zo7" />
        {label}
      </span>;
  };
  const filterSections: FilterSection[] = [{
    id: 'themes',
    title: 'Themes',
    type: 'checkbox',
    options: allThemes.map(theme => ({
      value: theme,
      label: theme
    })),
    value: selectedThemes,
    onChange: val => toggleTheme(val)
  }, {
    id: 'countries',
    title: 'Countries',
    type: 'checkbox',
    options: allCountries.map(c => ({
      value: c,
      label: c
    })),
    value: selectedCountries,
    onChange: val => toggleCountry(val)
  }, {
    id: 'frequency',
    title: 'Frequency',
    type: 'checkbox',
    options: allFrequencies.map(f => ({
      value: f,
      label: f
    })),
    value: selectedFrequencies,
    onChange: val => toggleFrequency(val)
  }];
  return <div className={`min-h-screen ${dk.page} ${dk.textPrimary}`} data-id="element-7480" data-mp="385yu18saihw4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-7481" data-mp="1iraitavfr5zo">
        {/* Page Header */}
        <div className="mb-8" data-id="element-7482" data-mp="1ds7c7lzt4rpl">
          <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-7483" data-mp="1dsza53gvn1j1">
            Book Boxes
          </h1>
          <p className={`${dk.textSecondary} max-w-3xl`} data-id="element-7484" data-mp="3pglxuvdw4q7g">
            Discover curated book subscription boxes filled with books and
            themed goodies. Find your next monthly obsession.
          </p>
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6" data-id="element-7485" data-mp="3koufh84w1qse">
          <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md transition-colors ${dk.card} ${dk.border} ${dk.textSecondary} ${dk.dropdownItemHover}`} data-id="element-7486" data-mp="3knjd9cr3cr3e">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-7487" data-mp="2wr57s4dqtlee" />
            <span data-id="element-7488" data-mp="2wsgb633a0r4z">Filters</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-7489" data-mp="31pgl4p38ye1d">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-7490" data-mp="uz0lp9ixdibl">
            <FilterSidebar title="Filters" sections={filterSections} onReset={resetFilters} data-id="element-7491" data-mp="njr2oafb2lvy" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-7492" data-mp="ei9f66zkjhx8">
            {/* Search and View Toggle */}
            <div className={`rounded-xl shadow-sm p-4 mb-8 ${dk.card} border ${dk.border}`} data-id="element-7493" data-mp="jjp0m05nrqg5">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-7494" data-mp="1776x28zvbcpg">
                <div className="relative flex-1" data-id="element-7495" data-mp="1b9tqgec6t5or">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-7496" data-mp="xbddw6rn8kyp" />
                  <input type="text" placeholder="Search book boxes by name, theme, or description..." className={`pl-10 pr-4 py-2.5 w-full rounded-lg border focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-7497" data-mp="1283ssqrppaij" />
                </div>
                <div className={`flex rounded-lg p-1 ${dk.surfaceMuted}`} data-id="element-7498" data-mp="3ohcorbl9p5ay">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} ${dk.dropdownItemHover}`}`} aria-label="Grid View" data-id="element-7499" data-mp="3of8i4lvzhs6z">
                    <GridIcon className="h-5 w-5" data-id="element-7500" data-mp="35igrtsi0yctf" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} ${dk.dropdownItemHover}`}`} aria-label="List View" data-id="element-7501" data-mp="35kjsd6gszoce">
                    <ListIcon className="h-5 w-5" data-id="element-7502" data-mp="2818kjhkm2emw" />
                  </button>
                </div>
              </div>
            </div>

            {/* Section Header */}
            <div className="flex items-center justify-between mb-6" data-id="element-7503" data-mp="gxgf6kgjt1ee">
              <div className="flex items-center gap-2" data-id="element-7504" data-mp="lqsu22vfdtty">
                <div className={`p-1.5 rounded-lg ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-7505" data-mp="ls40kag99for">
                  <SparklesIcon className="h-5 w-5" data-id="element-7506" data-mp="3p6zy4ahr5t39" />
                </div>
                <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-7507" data-mp="3p8t6pvbxendw">
                  New Book Boxes
                </h2>
              </div>
              <div className={`flex text-sm font-medium rounded-lg p-1 ${dk.surfaceMuted}`} data-id="element-7508" data-mp="2y71psjq8le4">
                {['All New', 'Just Added', 'Trending'].map(tab => {
                const id = tab.toLowerCase().replace(' ', '-');
                return <button key={id} onClick={() => setActiveTab(id)} className={`px-3 py-1.5 rounded-md transition-all ${activeTab === id ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} ${dk.dropdownItemHover}`}`} data-id="element-7509" data-mp="1if8vaw7wdrmw">
                      {tab}
                    </button>;
              })}
              </div>
            </div>

            {/* Results Grid/List */}
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1'}`} data-id="element-7510" data-mp="2k7biqrs0vtif">
              {filteredBookBoxes.map(box => {
              const contentData = getContentData(box.boxName);
              const themes = box.theme.split(',').map(t => t.trim());
              // GRID VIEW CARD
              if (viewMode === 'grid') {
                return <div key={box.boxName} className={`group rounded-xl overflow-hidden border transition-all duration-200 hover:shadow-md hover:-translate-y-1 ${dk.card} ${dk.border}`} data-id="element-7511" data-mp="9e79p2b5xmum">
                      {/* Image Area */}
                      <div className="aspect-[4/3] relative bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center" data-id="element-7512" data-mp="328xpfswj28x0">
                        {box.boxImage ? <img src={box.boxImage} alt={box.boxName} className="w-full h-full object-cover" data-id="element-7513" data-mp="2t8j10ko9odpa" /> : <BoxIcon className="h-16 w-16 text-white/50" data-id="element-7514" data-mp="2jd7ma2cuz6hj" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" data-id="element-7515" data-mp="25hn3r811kqan"></div>
                        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full border border-white/10 flex items-center gap-1" data-id="element-7516" data-mp="1vl0jakcmo195">
                          {box.shipping === 'Worldwide' ? 'Worldwide' : box.shipping}
                        </div>
                        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full border border-white/10" data-id="element-7517" data-mp="1c24nzijp1syj">
                          {contentData.ageRating}
                        </div>
                        <div className="absolute bottom-3 left-3" data-id="element-7518" data-mp="1zwnx8ggblk45">
                          {box.curatorImage ? <img src={box.curatorImage} alt={box.boxName} className="w-12 h-12 rounded-lg border-2 border-white object-cover shadow-md" data-id="element-7519" data-mp="1uy34t37b84q9" /> : <div className="w-12 h-12 rounded-lg border-2 border-white bg-indigo-600 flex items-center justify-center shadow-md" data-id="element-7520" data-mp="2iogoa5btwv98">
                              <BoxIcon className="h-5 w-5 text-white" data-id="element-7521" data-mp="2lq1vrea0l7a1" />
                            </div>}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4" data-id="element-7522" data-mp="2vmy7rtz4gbjc">
                        <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${rt.textPrimary}`} data-id="element-7523" data-mp="2vm508qsc9i05">
                          {box.frequency}
                        </div>
                        <Link to={`/book-boxes/${encodeURIComponent(box.boxName)}`} className={`block text-lg font-bold mb-2 transition-colors ${dk.textHeading} ${rt.textHover}`} data-id="element-7524" data-mp="39el762od206c">
                          {box.boxName}
                        </Link>
                        <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-7525" data-mp="1k1re5bbe82aa">
                          {themes.slice(0, 3).map((theme, idx) => <span key={idx} className={`text-[10px] px-2 py-0.5 ${rt.badgeRadius} border ${dk.badgeNeutral} ${dk.borderSubtle}`} data-id="element-7526" data-mp="64epiet0jwo6" data-mpi={idx}>
                              {theme}
                            </span>)}
                        </div>
                        <div className={`flex items-center text-xs mb-4 ${dk.textMuted}`} data-id="element-7527" data-mp="3ojz3n477c39j">
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-7528" data-mp="l1ypt8eyt3o0" />
                          {box.locationCountry}
                        </div>
                        <div className={`h-px w-full mb-3 ${dk.border}`} data-id="element-7529" data-mp="g1b0qsaaqwwf"></div>
                        <div className="flex items-center justify-between" data-id="element-7530" data-mp="b4av2ihczw5u">
                          <div className={`font-bold ${dk.textHeading}`} data-id="element-7531" data-mp="30sjw8x0kivrf">
                            {formatCurrency(box.price, box.currency)}
                          </div>
                          {box.ratings && <div className="flex items-center text-xs font-medium text-amber-400" data-id="element-7532" data-mp="zqc9wpr6zpn5">
                              <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-7533" data-mp="14pynj0axn7wh" />
                              {box.ratings.average}
                              <span className={`ml-1 ${dk.textFaint}`} data-id="element-7534" data-mp="pu8z6vi9yiol">
                                ({box.ratings.count})
                              </span>
                            </div>}
                        </div>
                      </div>
                    </div>;
              }
              // LIST VIEW CARD
              const isExpanded = expandedBoxId === box.boxName;
              return <div key={box.boxName} className={`rounded-xl border transition-all ${dk.card} ${dk.border}`} data-id="element-7535" data-mp="1dplest9y6xbt">
                    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-7536" data-mp="266e9ieuwls1f">
                      <div className="relative flex-shrink-0" data-id="element-7537" data-mp="3qx37sh0ckze2">
                        <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center overflow-hidden" data-id="element-7538" data-mp="3qvi9cgawwwax">
                          {box.boxImage ? <img src={box.boxImage} alt={box.boxName} className="w-full h-full object-cover" data-id="element-7539" data-mp="4gp4kuwzd7y8" /> : <BoxIcon className="h-8 w-8 text-white/50" data-id="element-7540" data-mp="32zxewl3k329v" />}
                        </div>
                        <div className="absolute -bottom-1 -right-1 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md border border-white/10" data-id="element-7541" data-mp="3h0gkllkjtw8v">
                          {contentData.ageRating}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7542" data-mp="s7uopm9yemec">
                        <div className="flex items-center flex-wrap gap-2 mb-1" data-id="element-7543" data-mp="s9fekylbph5v">
                          <Link to={`/book-boxes/${encodeURIComponent(box.boxName)}`} className={`text-lg font-bold hover:underline ${dk.textHeading}`} data-id="element-7544" data-mp="h0h9838s1y1b">
                            {box.boxName}
                          </Link>
                          <span className={dk.textFaint} data-id="element-7545" data-mp="m1n5uc5k65g4">|</span>
                          <span className={`text-sm ${dk.textSecondary}`} data-id="element-7546" data-mp="zxqulpxnxll7">
                            {box.theme}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm" data-id="element-7547" data-mp="19p4s5s620bbp">
                          <div className="flex items-center gap-2" data-id="element-7548" data-mp="14q1vow453dfc">
                            <span className={`font-bold ${dk.textHeading}`} data-id="element-7549" data-mp="14rv52j650br1">
                              {formatCurrency(box.price, box.currency)}
                            </span>
                            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${dk.badgeNeutral}`} data-id="element-7550" data-mp="1epj0zcfhbpas">
                              {box.frequency}
                            </span>
                          </div>
                          {box.ratings && <div className="flex items-center text-amber-400" data-id="element-7551" data-mp="1odxzgn1bs7nn">
                              <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-7552" data-mp="1t8twy2pbzn85" />
                              <span className={dk.textSecondary} data-id="element-7553" data-mp="1ek7h7zvsktge">
                                {box.ratings.average}
                              </span>
                            </div>}
                        </div>
                      </div>
                      <button onClick={() => toggleExpand(box.boxName)} className={`p-2 rounded-full transition-colors self-end sm:self-center ${dk.textMuted} ${dk.dropdownItemHover}`} data-id="element-7554" data-mp="1ajduvaxzzmpm">
                        {isExpanded ? <ChevronUpIcon className="h-5 w-5" data-id="element-7555" data-mp="hhmdeqbez037" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-7556" data-mp="cqe55xltlkrm" />}
                      </button>
                    </div>

                    {isExpanded && <div className={`border-t p-4 sm:p-6 ${dk.border} ${dk.surfaceMuted}`} data-id="element-7557" data-mp="2sqaf7o7sd9g">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-7558" data-mp="10fqc0qtodgqr">
                          <div className="lg:col-span-2 space-y-6" data-id="element-7559" data-mp="vigq3l5hvn7o">
                            <div data-id="element-7560" data-mp="qhj9hd04v18a">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.textMuted}`} data-id="element-7561" data-mp="qgrb5uf5ipeb">
                                About
                              </h4>
                              <p className={`text-sm leading-relaxed ${dk.textSecondary}`} data-id="element-7562" data-mp="3696ty8xyn1a7">
                                {box.description}
                              </p>
                            </div>
                            <div data-id="element-7563" data-mp="1qo68eja0ct7f">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${dk.textMuted}`} data-id="element-7564" data-mp="1vpltgbc6qzpt">
                                Content Highlights
                              </h4>
                              <div className="flex flex-wrap gap-2" data-id="element-7565" data-mp="25evitjfna4a8">
                                {contentData.tropes.map(trope => <ContentTagPill key={trope} type="trope" label={trope} data-id="element-7566" data-mp="2efbnuxc1djh7" />)}
                                {contentData.representation.map(rep => <ContentTagPill key={rep} type="rep" label={rep} data-id="element-7567" data-mp="p1f5w3lb3t7v" />)}
                                {contentData.warnings.map(warn => <ContentTagPill key={warn} type="warning" label={warn} data-id="element-7568" data-mp="2kh3uka06ebh6" />)}
                                {themes.map(theme => <ContentTagPill key={theme} type="theme" label={theme} data-id="element-7569" data-mp="25kcudyiogvqi" />)}
                              </div>
                            </div>
                          </div>
                          <div className={`lg:border-l pl-0 lg:pl-8 space-y-5 ${dk.border}`} data-id="element-7570" data-mp="190429edtvwn5">
                            <div data-id="element-7571" data-mp="13prvb9t6cxn2">
                              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.textMuted}`} data-id="element-7572" data-mp="13qt8pkxnxw7y">
                                Details
                              </h4>
                              <ul className="space-y-2 text-sm" data-id="element-7573" data-mp="1ho81mzn4km4o">
                                <li className="flex items-start" data-id="element-7574" data-mp="1mnkohkdzxt79">
                                  <GlobeIcon className={`h-4 w-4 mr-2 mt-0.5 ${dk.textMuted}`} data-id="element-7575" data-mp="1mn2me74nkkrf" />
                                  <span className={dk.textSecondary} data-id="element-7576" data-mp="1colosrb7l7we">
                                    Based in {box.locationCountry}
                                  </span>
                                </li>
                                <li className="flex items-start" data-id="element-7577" data-mp="1b146dg7gztr">
                                  <TruckIcon className={`h-4 w-4 mr-2 mt-0.5 ${dk.textMuted}`} data-id="element-7578" data-mp="23wn6q6v9fswv" />
                                  <span className={dk.textSecondary} data-id="element-7579" data-mp="1yzwslp30n7e3">
                                    Ships: {box.shipping}
                                  </span>
                                </li>
                                <li className="flex items-start" data-id="element-7580" data-mp="1p6680va0fh3o">
                                  <CalendarIcon className={`h-4 w-4 mr-2 mt-0.5 ${dk.textMuted}`} data-id="element-7581" data-mp="1kah5qk6lvm3o" />
                                  <span className={dk.textSecondary} data-id="element-7582" data-mp="1b7g0elpkdz12">
                                    {box.frequency} Subscription
                                  </span>
                                </li>
                                {box.oneTimePurchase && <li className="flex items-start" data-id="element-7583" data-mp="30n5yrmgplnvl">
                                    <CheckIcon className="h-4 w-4 mr-2 mt-0.5 text-green-500" data-id="element-7584" data-mp="30nyw5wts3112" />
                                    <span className={dk.textSecondary} data-id="element-7585" data-mp="386ey0mvi466x">
                                      One-time purchase available
                                    </span>
                                  </li>}
                              </ul>
                            </div>
                            <div className="pt-2" data-id="element-7586" data-mp="3mxfhdmxsdkhu">
                              <Link to={`/book-boxes/${encodeURIComponent(box.boxName)}`} className={`block w-full text-center py-2 rounded-lg font-medium transition-colors text-white ${rt.primaryButton}`} data-id="element-7587" data-mp="2p8vwe4bdhpuj">
                                View Full Details
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>;
            })}
            </div>

            {filteredBookBoxes.length === 0 && <div className={`text-center py-16 rounded-xl border border-dashed ${dk.card} ${dk.border}`} data-id="element-7588" data-mp="zjtuoztkf0jc">
                <BoxIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-7589" data-mp="1e9rnxu6cqgfe" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-7590" data-mp="19hhqzu6tkg3m">
                  No book boxes found
                </h3>
                <p className={`max-w-md mx-auto ${dk.textSecondary}`} data-id="element-7591" data-mp="1n7tjvvb8aivt">
                  Try adjusting your search or filters to find what you're
                  looking for.
                </p>
                <button onClick={resetFilters} className={`mt-6 px-4 py-2 text-white rounded-md transition-colors ${rt.primaryButton}`} data-id="element-7592" data-mp="1x6991sqp3i2o">
                  Clear Filters
                </button>
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default BookBoxes;